// src/components/local/HabilitacionLocalForm.js
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import {
  updateAfiliadoEstado,
  createHabilitacionLocalLog,
  fetchLogsByAfiliado,
} from "../../reducers/habilitacionLocal_slice";

import { fetchLugarById } from "../../reducers/lugar_slice";
import { fetchAfiliadosByLugar } from "../../reducers/afiliado_slice";

// ⚠️ Misma ruta/contrato que en la lista
import CustomPrintModal from "../UI/CustomPrintModal";

const SITUACION_TRASLADO_LOCAL_ID = 3;
const MOTIVO_DEUDOR = "ES DEUDOR DE CUOTAS AL COLEGIO LOCAL";
const MOTIVO_PAGO = "PAGO LAS CUOTAS ADEUDADAS..";

const HabilitacionLocalForm = ({ afiliado, onClose }) => {
  const dispatch = useDispatch();
  const sesion = useSelector((s) => s.sesion);
  const afiliadoGestor = useSelector((s) => s.afiliado.current);
  const logs = useSelector((s) => s.habilitacionLocal.logsByAfiliado[afiliado?.id]);
  const lugarActual = useSelector((s) => s.lugar.current);

  const estadoActual = (afiliado?.activo || "INACTIVO").toUpperCase();
  const [estado, setEstado] = useState(estadoActual);
  const [motivo, setMotivo] = useState(
    estadoActual === "ACTIVO" ? MOTIVO_DEUDOR : MOTIVO_PAGO
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successInfo, setSuccessInfo] = useState(null); // { desde, hacia, motivo, fecha }

  // Orden del historial por fecha/hora
  const [sortDir, setSortDir] = useState("DESC"); // DESC = más recientes primero

  // Modal de impresión (mismo contrato que la lista)
  const [showPrint, setShowPrint] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });

  // ------- EFECTOS: carga de logs y lugar --------
  useEffect(() => {
    if (afiliado?.id) {
      dispatch(fetchLogsByAfiliado(afiliado.id));
    }
    if (afiliado?.id_lugar_residencia) {
      const needsLugarFetch =
        !lugarActual || Number(lugarActual?.id) !== Number(afiliado.id_lugar_residencia);
      if (needsLugarFetch) {
        dispatch(fetchLugarById(afiliado.id_lugar_residencia));
      }
    }
  }, [dispatch, afiliado?.id, afiliado?.id_lugar_residencia]); // no incluyas lugarActual para evitar loops

  // ------- REGLAS -------
  const isTrasladoLocal = Number(afiliado?.id_situacion) === SITUACION_TRASLADO_LOCAL_ID;
  const sameCity =
    Number(afiliadoGestor?.id_lugar_residencia) === Number(afiliado?.id_lugar_residencia);
  const allowChange = isTrasladoLocal && sameCity;

  const nombreLugar = useMemo(() => {
    return lugarActual?.nombre || afiliado?.lugar_residencia?.nombre || "—";
  }, [lugarActual?.nombre, afiliado?.lugar_residencia?.nombre]);

  // ------- Prefill dinámico del MOTIVO según transición -------
  useEffect(() => {
    // ACTIVO -> INACTIVO => DEUDOR
    if (estadoActual === "ACTIVO" && estado === "INACTIVO") {
      setMotivo(MOTIVO_DEUDOR);
      return;
    }
    // INACTIVO -> ACTIVO => PAGO
    if (estadoActual === "INACTIVO" && estado === "ACTIVO") {
      setMotivo(MOTIVO_PAGO);
      return;
    }
    // Si no cambia, no tocamos el texto que haya escrito el usuario
  }, [estado, estadoActual]);

  // ------ helper: mostrar nombre de usuario en historial ------
  const getUsuarioLabel = (r) => {
    if (r?.Usuario?.usuario) return r.Usuario.usuario;
    if (r?.usuario && typeof r.usuario === "object" && r.usuario.usuario) return r.usuario.usuario;
    if (typeof r?.usuario === "string") return r.usuario;
    if (r?.user?.usuario) return r.user.usuario;
    if (r?.usuario_nombre) return r.usuario_nombre;
    return r?.id_usuario ?? "—";
  };

  // ------ ordenar logs por createdAt según sortDir ------
  const sortedRows = useMemo(() => {
    const rows = logs?.rows ? [...logs.rows] : [];
    rows.sort((a, b) => {
      const da = new Date(a.createdAt).getTime();
      const db = new Date(b.createdAt).getTime();
      return sortDir === "ASC" ? da - db : db - da;
    });
    return rows;
  }, [logs?.rows, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));

  // ------ datos para impresión (MISMO CONTRATO QUE EN LA LISTA) ------
  const title1 = `COLEGIO LOCAL — ${nombreLugar.toUpperCase()}`;
  const title2 = `${afiliado?.persona?.apellido1 || ""} ${afiliado?.persona?.apellido2 || ""}, ${
    afiliado?.persona?.nombre || ""
  } — Reg. ${afiliado?.num_reg || "—"}`;

  useEffect(() => {
    // Construimos content solo cuando abrimos el modal o cambia el orden
    const head = [
      [
        "Fecha",
        "Hora",
        "Desde",
        "Hacia",
        "Usuario",
        "Motivo",
      ],
    ];
    const body = (sortedRows || []).map((r) => {
      const d = new Date(r.createdAt);
      return [
        d.toLocaleDateString(),
        d.toLocaleTimeString(),
        r.desde_estado,
        r.hacia_estado,
        getUsuarioLabel(r),
        r.motivo || "—",
      ];
    });
    setPrintContent({ head, body });
  }, [sortedRows, nombreLugar, afiliado?.num_reg, afiliado?.persona]);

  // ------- HANDLERS -------
  const handleGuardar = async () => {
    try {
      setSaving(true);
      setError("");

      if (!allowChange) {
        setError(
          "No se puede cambiar el estado. Solo es válido para afiliados con situación TRASLADO LOCAL y que residan en el mismo Colegio Local del gestor."
        );
        setSaving(false);
        return;
      }

      // Debe haber cambio de estado
      if (estado === estadoActual) {
        setError("Seleccione un estado diferente al actual para poder guardar.");
        setSaving(false);
        return;
      }

      // Motivo obligatorio
      if (!motivo || !motivo.trim()) {
        setError("El motivo es obligatorio. Por favor, indique la razón del cambio.");
        setSaving(false);
        return;
      }

      const desde = estadoActual;
      const hacia = estado;

      // 1) Actualiza estado del afiliado
      await dispatch(
        updateAfiliadoEstado({ idAfiliado: afiliado.id, estado: hacia })
      ).unwrap();

      // 2) Registra log de auditoría (con motivo)
      await dispatch(
        createHabilitacionLocalLog({
          id_afiliado: afiliado.id,
          id_usuario: sesion?.id,
          desde_estado: desde,
          hacia_estado: hacia,
          motivo: motivo.trim(),
        })
      ).unwrap();

      // 3) Refrescar listado visible (por residencia del gestor)
      const idLugarGestor = Number(afiliadoGestor?.id_lugar_residencia);
      if (idLugarGestor) {
        await dispatch(fetchAfiliadosByLugar(idLugarGestor)).unwrap();
      }

      // 4) Refrescar logs (para que aparezca el nuevo al instante)
      await dispatch(fetchLogsByAfiliado(afiliado.id));

      // 5) Mostrar confirmación y recién luego salir al List
      setSuccessInfo({
        desde,
        hacia,
        motivo: motivo.trim(),
        fecha: new Date(),
      });
    } catch (e) {
      setError(e?.error || e?.message || "Ocurrió un error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleContinuar = () => {
    onClose(); // cerrar y volver a la lista
  };

  const guardarDisabled = !allowChange || saving || estado === estadoActual;

  return (
    <>
      <Modal show onHide={onClose} backdrop="static" size="lg">
        <Modal.Header closeButton style={{ backgroundColor: "#255957", color: "white" }}>
          <Modal.Title>
            COLEGIO LOCAL — {nombreLugar} &nbsp;|&nbsp; Núm. Reg.:{" "}
            <Badge bg="secondary">{afiliado?.num_reg || "—"}</Badge>
            <br />
            <span className="fw-normal">
              {afiliado?.persona?.apellido1} {afiliado?.persona?.apellido2}, {afiliado?.persona?.nombre}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Mensaje de confirmación tras guardar */}
          {successInfo ? (
            <div className="alert alert-success" role="alert">
              <h6 className="mb-2">¡Cambio registrado correctamente!</h6>
              <div className="small">
                <div>
                  <strong>Afiliado:</strong> {afiliado?.persona?.apellido1} {afiliado?.persona?.apellido2},{" "}
                  {afiliado?.persona?.nombre} (Reg. <b>{afiliado?.num_reg || "—"}</b>)
                </div>
                <div>
                  <strong>Colegio Local:</strong> {nombreLugar}
                </div>
                <div className="mt-2">
                  <strong>Desde:</strong> {successInfo.desde} &nbsp;→&nbsp; <strong>Hacia:</strong> {successInfo.hacia}
                </div>
                <div>
                  <strong>Motivo:</strong> {successInfo.motivo}
                </div>
                <div className="text-muted mt-1">
                  <strong>Fecha:</strong> {successInfo.fecha.toLocaleString()}
                </div>
              </div>
            </div>
          ) : (
            <>
              {!allowChange && (
                <div className="alert alert-warning mb-3" role="alert">
                  Solo se puede cambiar el estado de <b>TRASLADO LOCAL</b> que residan en tu mismo Colegio Local.
                </div>
              )}

              {error && <div className="alert alert-danger">{error}</div>}

              <div className="mb-2 small text-muted">
                Situación:&nbsp;
                <Badge bg={isTrasladoLocal ? "success" : "secondary"}>
                  {isTrasladoLocal ? "TRASLADO LOCAL" : `ID ${afiliado?.id_situacion || "—"}`}
                </Badge>
                &nbsp; • &nbsp; Residencia:&nbsp;
                <b>
                  {nombreLugar} (ID: {afiliado?.id_lugar_residencia ?? "—"})
                </b>
                &nbsp; • &nbsp; Estado actual:&nbsp;
                <Badge bg={estadoActual === "ACTIVO" ? "success" : "secondary"}>
                  {estadoActual}
                </Badge>
              </div>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    disabled={!allowChange || saving}
                  >
                    <option value="ACTIVO">ACTIVO</option>
                    <option value="INACTIVO">INACTIVO</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Selecciona el nuevo estado que tendrá el afiliado en este Colegio Local.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Motivo <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Describe la razón del cambio…"
                    disabled={saving}
                    required
                  />
                  <Form.Text className="text-muted">
                    El motivo es obligatorio. Se predefine según el cambio, pero puedes editarlo.
                  </Form.Text>
                </Form.Group>
              </Form>

              <hr />

              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">Historial de cambios</h6>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={toggleSort}
                    title="Cambiar orden por fecha/hora"
                  >
                    {sortDir === "DESC" ? "Más recientes primero" : "Más antiguos primero"}
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowPrint(true)}
                    title="Imprimir historial"
                    disabled={!logs?.rows || logs.rows.length === 0}
                  >
                    Imprimir historial
                  </Button>
                </div>
              </div>

              <div className="small text-muted mb-2">
                Click en <b>Fecha</b> para alternar el orden.
              </div>

              {!logs && <div className="text-muted">Cargando…</div>}
              {logs?.rows?.length === 0 && <div className="text-muted">Sin registros</div>}
              {logs?.rows?.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th style={{ cursor: "pointer" }} onClick={toggleSort}>Fecha</th>
                        <th>Hora</th>
                        <th>Desde</th>
                        <th>Hacia</th>
                        <th>Usuario</th>
                        <th>Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((r) => {
                        const d = new Date(r.createdAt);
                        return (
                          <tr key={r.id}>
                            <td>{d.toLocaleDateString()}</td>
                            <td>{d.toLocaleTimeString()}</td>
                            <td>{r.desde_estado}</td>
                            <td>{r.hacia_estado}</td>
                            <td>
                              {r?.Usuario?.usuario ||
                                (r?.usuario && typeof r.usuario === "object" && r.usuario.usuario) ||
                                (typeof r?.usuario === "string" ? r.usuario : r?.id_usuario ?? "—")}
                            </td>
                            <td>{r.motivo || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {successInfo ? (
            <Button variant="primary" onClick={handleContinuar}>
              Continuar
            </Button>
          ) : (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => setShowPrint(true)}
                disabled={!logs?.rows || logs.rows.length === 0}
                title="Imprimir historial"
              >
                Imprimir historial
              </Button>
              <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleGuardar} disabled={!allowChange || saving || estado === estadoActual}>
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal de impresión — MISMO CONTRATO QUE EN LA LISTA */}
      <CustomPrintModal
        show={showPrint}
        handleClose={() => setShowPrint(false)}
        title1={title1}
        title2={title2}
        content={printContent}
        copia={false}
      />
    </>
  );
};

export default HabilitacionLocalForm;
